import { Button, CircularProgress, Radio, Stack, styled } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowSelectionModel, GridSlotProps, useGridApiContext } from '@mui/x-data-grid';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useInterval } from 'react-use';
import { PageContainer } from 'src/components/common/atoms/PageContainer';
import { KanriPageContainer } from 'src/components/kanri/atoms/KanriPageContainer';
import {
  GridRow,
  SearchType,
} from 'src/components/kanri/pages/jichitai/PrsStatusReportPage/types';
import { useQuery } from 'src/components/kanri/pages/jichitai/PrsStatusReportPage/useQuery';
import { appConst, RoleId } from 'src/constants/common';
import { SnackbarMessageId, snackbarMessages } from 'src/constants/snackbarMessages';
import { useNavigateSystemError } from 'src/hooks/common/useNavigateSystemError';
import { useUserData } from 'src/hooks/common/useUserData';
import { standardColors } from 'src/themes/standardColors';
import { saveBlob } from 'src/utilities/download';
import { ErrorResponse } from 'src/utilities/restApi/common/types/responses';
import { downloadReportPrsStatus } from 'src/utilities/restApi/kanri/report/downloadReportPrsStatus';
import { ReportJobState } from 'src/utilities/restApi/kanri/types/reportPrsStatus';

const reportJobStateLabels: Record<ReportJobState, string> = {
  processing: '作成中',
  completed: '作成完了',
  failed: 'エラー',
};

const StyledCircularProgress = styled(CircularProgress)(() => ({
  '.MuiCircularProgress-circle': {
    color: standardColors.white,
  },
}));

// グリッドのチェックボックスをラジオボタンに置き換える
const RadioCheckbox = (props: GridSlotProps['baseCheckbox']) => {
  const apiRef = useGridApiContext();

  return (
    <Radio
      {...props}
      size="small"
      onClick={(e) => {
        if (props.checked) {
          // 選択済みのラジオボタンのチェックを外す
          e.preventDefault();
          apiRef.current.setRowSelectionModel([]);
        }
      }}
    />
  );
};

export const PrsStatusReportPage = () => {
  const { enqueueSnackbar } = useSnackbar();
  const navigateSystemError = useNavigateSystemError();
  const { userData } = useUserData();
  const isJichitaiStaff = (userData?.roleId === RoleId.JichitaiStaff);

  // states
  const [searchType, setSearchType] = useState<SearchType>('search');
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 20,
  });

  const [isDownloading, setIsDownloading] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] = useState<GridRowSelectionModel>([]);

  // custom hooks
  const {
    isLoading,
    rows,
    pageInfo,
    error: searchError,
    search,
  } = useQuery({ paginationModel });

  // intervalによる画面更新
  useInterval(
    () => {
      setSearchType('refresh');
      search('system');
    },
    isLoading ? null : appConst.POLLING_INTERVAL
  );

  const rowCountRef = useRef(pageInfo?.totalRowCount || 0);

  const rowCount = useMemo(() => {
    if (pageInfo?.totalRowCount !== undefined) {
      rowCountRef.current = pageInfo.totalRowCount;
    }
    return rowCountRef.current;
  }, [pageInfo?.totalRowCount]);


  const handlePaginationModelChange = (model: typeof paginationModel) => {
    // 自動更新が走ると'refresh'がセットされるため、
    // ユーザーがページング操作をしたときは 'search' に戻す
    setSearchType('search');
    setPaginationModel(model);
  };


  // ダウンロードボタン押下
  const downloadReportButtonOnClick = async () => {
    const selectedRowId = rowSelectionModel[0];

    if (selectedRowId === undefined) {
      enqueueSnackbar('ダウンロードする行を選択してください。', {
        variant: 'warning',
      });
      return;
    }

    const selectedRow = rows.find((row) => row.id === selectedRowId);

    if (!selectedRow?.jobId) {
      enqueueSnackbar('ダウンロード対象のジョブがありません。', {
        variant: 'error',
      });
      return;
    }

    setIsDownloading(true);

    try {

      // ダウンロード処理
      const blob = await downloadReportPrsStatus(String(selectedRow.jobId));
      const zipName = (selectedRow.fileName ?? 'report.csv').replace(/\.csv$/, '.zip');
      saveBlob(blob, zipName)

      enqueueSnackbar(selectedRow.fileName + 'をダウンロードしました。', { variant: 'success' });

    } catch (e) {
      const reason : string = (e as ErrorResponse)?.error?.reason;
      enqueueSnackbar(reason ?? '安否状況レポートをダウンロードできません。', {variant: 'error',});
    } finally {
      setIsDownloading(false);
    }
  };  

  const columns: GridColDef<GridRow>[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 90 },
      {
        field: 'fileName',
        headerName: 'ファイル名',
        width: 330,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: 'state',
        headerName: 'ステータス',
        width: 120,
        disableColumnMenu: true,
        sortable: false,
        renderCell: (params: GridRenderCellParams<GridRow, ReportJobState>) => {
          return params.value ? reportJobStateLabels[params.value] : '-';
        }
      },
      {
        field: 'rowCount',
        headerName: '行数',
        width: 100,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: 'jobStart',
        headerName: '作成開始日時',
        width: 170,
        disableColumnMenu: true,
        sortable: false,
      },
      {
        field: 'jobEnd',
        headerName: '作成完了日時',
        width: 170,
        disableColumnMenu: true,
        sortable: false,
      },
    ],
    []
  );

  // useEffect
  useEffect(() => {
    if (searchError.isError) {
      if (![axios.AxiosError.ERR_CANCELED, axios.AxiosError.ECONNABORTED].includes(searchError.reason ?? '')) {
        navigateSystemError();
      } else if (searchError.reason === axios.AxiosError.ECONNABORTED) {
        const snackbarMessage = snackbarMessages[SnackbarMessageId.SearchTimeOut];

        if (snackbarMessage) {
          enqueueSnackbar(snackbarMessage.message, {
            variant: snackbarMessage.variant,
          });
        }
      }
    }
  }, [searchError]);

  return (
    <PageContainer title="安否状況レポート | mutual-assistance">
      <KanriPageContainer title="安否状況レポート">
        <Stack spacing={2} sx={{ overflow: 'auto', height: '100%' }}>
          <Stack flexGrow={1} sx={{ overflow: 'auto' }}>
            <DataGrid
              rows={rows}
              columns={columns}
              rowCount={rowCount}
              loading={!!isLoading}
              checkboxSelection
              disableRowSelectionOnClick
              disableMultipleRowSelection
              paginationModel={paginationModel}
              paginationMode="server"
              onPaginationModelChange={handlePaginationModelChange}
              rowSelectionModel={rowSelectionModel}
              onRowSelectionModelChange={setRowSelectionModel}
              sortingMode="server"
              initialState={{
                columns: {
                  columnVisibilityModel: {
                    id: false,
                  },
                },
              }}
              localeText={{
                noRowsLabel: '安否状況レポートはありません',
              }}
              slots={{ baseCheckbox: RadioCheckbox }}
              isRowSelectable={(params) => params.row.state === 'completed'}
              sx={{
                '& .MuiDataGrid-columnHeaderCheckbox .MuiDataGrid-columnHeaderTitleContainer': {
                  display: 'none',
                },
              }}
              slotProps={{
                loadingOverlay: {
                  variant: searchType === 'search' ? 'circular-progress' : 'linear-progress',
                  noRowsVariant: 'skeleton',
                  sx: searchType === 'search' ? {} : { backgroundColor: 'transparent' },
                },
                ...(isLoading === 'user'
                  ? {
                      pagination: {
                        backIconButtonProps: {
                          disabled: true,
                        },
                        nextIconButtonProps: {
                          disabled: true,
                        },
                      },
                    }
                  : {}),
              }}
            />
          </Stack>
          {isJichitaiStaff && (
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={3}>
              <Button variant="contained" disabled={isDownloading || rowSelectionModel.length === 0}
               onClick={downloadReportButtonOnClick}
               sx={{ minWidth: 180, height: 36.5 }}>
                {isDownloading ? <StyledCircularProgress size={14.5} /> : "ダウンロード"}
              </Button>
            </Stack>
          )}
        </Stack>
      </KanriPageContainer>
    </PageContainer>
  );
};
